/**
 * /mcp-setup — Configuração do MCP server pra agents IA.
 *
 * 1. Gera ou seleciona uma API key
 * 2. Pra cada agent suportado (Claude Code/Desktop, Cursor, Codex, OpenCode):
 *    - Mostra config JSON pronta
 *    - Botão "Copiar"
 *    - Deep link (se aplicável) ou comando CLI
 * 3. Lista tools/resources/prompts expostos
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Bot,
  Check,
  CheckCircle2,
  Copy,
  Cpu,
  ExternalLink,
  Key,
  Loader2,
  Plug,
  Sparkles,
  Terminal,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { apiKeysApi, type ApiKeyCreated } from "@/lib/api-system";
import { cn } from "@/lib/utils";

const API_BASE = "https://api-one.encontreumnerd.com.br";

type AgentId =
  | "claude-code"
  | "claude-desktop"
  | "cursor"
  | "codex"
  | "opencode"
  | "windsurf"
  | "zed";

interface AgentDef {
  id: AgentId;
  name: string;
  description: string;
  icon: typeof Bot;
  configType: "cli" | "json" | "yaml";
  buildConfig: (apiKey: string) => string;
  buildCli?: (apiKey: string) => string;
  deepLink?: (apiKey: string) => string | null;
  docs?: string;
}

const mcpEnv = (apiKey: string) => ({
  OPNEXT_API_KEY: apiKey || "SUA_API_KEY_AQUI",
  OPNEXT_API_URL: API_BASE,
});

const AGENTS: AgentDef[] = [
  {
    id: "claude-code",
    name: "Claude Code (CLI)",
    description: "Adicionar via comando `claude mcp add`",
    icon: Terminal,
    configType: "cli",
    buildConfig: (apiKey) =>
      JSON.stringify(
        {
          mcpServers: {
            "op-next": {
              command: "uvx",
              args: ["op-next-mcp"],
              env: mcpEnv(apiKey),
            },
          },
        },
        null,
        2,
      ),
    buildCli: (apiKey) =>
      `claude mcp add op-next uvx op-next-mcp \\
  --env OPNEXT_API_KEY=${apiKey || "SUA_API_KEY"} \\
  --env OPNEXT_API_URL=${API_BASE}`,
    docs: "https://docs.claude.com/en/docs/claude-code/mcp",
  },
  {
    id: "claude-desktop",
    name: "Claude Desktop",
    description: "Edite claude_desktop_config.json",
    icon: Bot,
    configType: "json",
    buildConfig: (apiKey) =>
      JSON.stringify(
        {
          mcpServers: {
            "op-next": {
              command: "uvx",
              args: ["op-next-mcp"],
              env: mcpEnv(apiKey),
            },
          },
        },
        null,
        2,
      ),
    docs: "https://modelcontextprotocol.io/quickstart/user",
  },
  {
    id: "cursor",
    name: "Cursor",
    description: "Settings → MCP → Add Server (suporta deep link)",
    icon: Cpu,
    configType: "json",
    buildConfig: (apiKey) =>
      JSON.stringify(
        {
          "op-next": {
            command: "uvx",
            args: ["op-next-mcp"],
            env: mcpEnv(apiKey),
          },
        },
        null,
        2,
      ),
    deepLink: (apiKey) => {
      const cfg = btoa(
        JSON.stringify({
          command: "uvx",
          args: ["op-next-mcp"],
          env: mcpEnv(apiKey),
        }),
      );
      return `cursor://anysphere.cursor-deeplink/mcp/install?name=op-next&config=${cfg}`;
    },
    docs: "https://docs.cursor.com/mcp",
  },
  {
    id: "codex",
    name: "Codex CLI",
    description: "`codex mcp add` ou ~/.codex/config.toml",
    icon: Terminal,
    configType: "cli",
    buildConfig: (apiKey) =>
      `[mcp_servers.op-next]
command = "uvx"
args = ["op-next-mcp"]

[mcp_servers.op-next.env]
OPNEXT_API_KEY = "${apiKey || "SUA_API_KEY"}"
OPNEXT_API_URL = "${API_BASE}"`,
    buildCli: (apiKey) =>
      `codex mcp add op-next uvx op-next-mcp \\
  --env OPNEXT_API_KEY=${apiKey || "SUA_API_KEY"} \\
  --env OPNEXT_API_URL=${API_BASE}`,
  },
  {
    id: "opencode",
    name: "OpenCode",
    description: "YAML em ~/.opencode/config.yaml",
    icon: Sparkles,
    configType: "yaml",
    buildConfig: (apiKey) => `mcp_servers:
  op-next:
    command: uvx
    args: [op-next-mcp]
    env:
      OPNEXT_API_KEY: ${apiKey || "SUA_API_KEY"}
      OPNEXT_API_URL: ${API_BASE}`,
  },
  {
    id: "windsurf",
    name: "Windsurf",
    description: "Cascade Settings → MCP Servers",
    icon: Cpu,
    configType: "json",
    buildConfig: (apiKey) =>
      JSON.stringify(
        {
          mcpServers: {
            "op-next": {
              command: "uvx",
              args: ["op-next-mcp"],
              env: mcpEnv(apiKey),
            },
          },
        },
        null,
        2,
      ),
    docs: "https://docs.codeium.com/windsurf/mcp",
  },
  {
    id: "zed",
    name: "Zed",
    description: "Settings → MCP Servers (extensions.json)",
    icon: Cpu,
    configType: "json",
    buildConfig: (apiKey) =>
      JSON.stringify(
        {
          context_servers: {
            "op-next": {
              command: {
                path: "uvx",
                args: ["op-next-mcp"],
                env: mcpEnv(apiKey),
              },
            },
          },
        },
        null,
        2,
      ),
  },
];

const TOOLS_LIST = [
  { category: "Dashboard", items: ["dashboard_stats", "search"] },
  {
    category: "Chamados",
    items: [
      "list_jobs", "get_job", "create_job", "transition_job",
      "assign_job", "duplicate_job", "dispatch_job", "comment_on_job",
    ],
  },
  { category: "Clientes", items: ["list_clients", "get_client", "create_client"] },
  { category: "Técnicos", items: ["list_nerds", "get_nerd", "nerd_skills"] },
  { category: "Faturas", items: ["list_invoices", "approve_invoice", "mark_invoice_paid"] },
  { category: "Reports", items: ["revenue_report", "top_clients", "sla_compliance"] },
  { category: "Notificações", items: ["my_notifications", "broadcast_notification"] },
];

export function McpSetupPage() {
  const keys = useQuery({ queryKey: ["api-keys-mcp"], queryFn: () => apiKeysApi.list() });
  const [selectedKey, setSelectedKey] = useState<string>("");
  const [createdKey, setCreatedKey] = useState<ApiKeyCreated | null>(null);

  const create = useMutation({
    mutationFn: () =>
      apiKeysApi.create({
        name: `MCP Agent — ${new Date().toLocaleDateString("pt-BR")}`,
        scopes: ["mcp", "read", "write"],
      }),
    onSuccess: (data) => {
      setCreatedKey(data);
      setSelectedKey(data.raw_key);
      keys.refetch();
      toast.success("API key gerada — copie agora");
    },
    onError: (e: Error) => toast.error("Falha", { description: e.message }),
  });

  const apiKey = createdKey?.raw_key ?? selectedKey;

  return (
    <>
      <PageHeader
        title="MCP — Conecte agents IA"
        description="Plugue Claude, Cursor, Codex, OpenCode e outras IAs no op-next via Model Context Protocol."
        breadcrumbs={[{ label: "Configurações", to: "/settings" }, { label: "MCP" }]}
      />

      {/* Step 1: API key */}
      <Section
        step={1}
        title="Gerar API key"
        description="Token de autenticação que o agent vai usar pra falar com a API. Salve ela na hora — não é mostrada novamente."
      >
        {createdKey ? (
          <div className="rounded-2xl border-2 border-amber-300/60 bg-amber-50/40 dark:bg-amber-500/[0.04] p-5">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-amber-600 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-sm">Chave criada · cópia ÚNICA</h4>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Salve agora. Não vai aparecer mais.
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <code className="flex-1 rounded-lg bg-white dark:bg-black/30 border border-amber-300/60 p-2.5 font-mono text-xs break-all">
                    {createdKey.raw_key}
                  </code>
                  <Button
                    size="icon-sm"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(createdKey.raw_key);
                      toast.success("Copiada");
                    }}
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <Button onClick={() => create.mutate()} disabled={create.isPending}>
              {create.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Key className="h-4 w-4" />
              )}
              Gerar nova API key pro MCP
            </Button>
            {keys.data && keys.data.items.length > 0 && (
              <span className="text-xs text-muted-foreground">
                ou use uma existente em{" "}
                <Link to="/integrations" className="text-primary-700 hover:underline">
                  Integrações
                </Link>
              </span>
            )}
          </div>
        )}
      </Section>

      {/* Step 2: Choose agent */}
      <Section
        step={2}
        title="Escolher o agent"
        description="Selecione seu IDE/CLI. Cada um tem um formato de config diferente."
      >
        <Tabs defaultValue="claude-code">
          <TabsList className="flex-wrap">
            {AGENTS.map((a) => (
              <TabsTrigger key={a.id} value={a.id}>
                <a.icon className="h-3.5 w-3.5" /> {a.name}
              </TabsTrigger>
            ))}
          </TabsList>
          {AGENTS.map((a) => (
            <TabsContent key={a.id} value={a.id}>
              <AgentCard agent={a} apiKey={apiKey} />
            </TabsContent>
          ))}
        </Tabs>
      </Section>

      {/* Step 3: What's exposed */}
      <Section
        step={3}
        title="O que o agent pode fazer"
        description="24 tools, 3 resources, 3 prompts pré-prontos cobrindo toda a operação."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {TOOLS_LIST.map((g) => (
            <div
              key={g.category}
              className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 p-4"
            >
              <p className="font-semibold text-sm mb-2">{g.category}</p>
              <ul className="space-y-1">
                {g.items.map((t) => (
                  <li key={t}>
                    <code className="text-xs font-mono text-muted-foreground">{t}</code>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-4 rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 p-5">
          <p className="font-semibold text-sm mb-2 flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5 text-amber-500" /> Prompts pré-prontos
          </p>
          <ul className="space-y-1.5 text-sm">
            <li><code className="font-mono text-xs">/atender_chamado</code> — pipeline pra atender um chamado</li>
            <li><code className="font-mono text-xs">/fechamento_mensal</code> — workflow financeiro mensal</li>
            <li><code className="font-mono text-xs">/briefing_diario</code> — KPIs + alertas operacionais</li>
          </ul>
        </div>
      </Section>

      {/* Step 4: Test */}
      <Section
        step={4}
        title="Testar"
        description="Após configurar, peça ao agent:"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <ExampleCard
            text='"Me dá um briefing operacional do dia"'
            hint="Usa dashboard_stats + list_jobs + sla_compliance"
          />
          <ExampleCard
            text='"Lista chamados abertos e sugere quem atribuir"'
            hint="list_jobs + list_nerds + ranking por proximidade"
          />
          <ExampleCard
            text='"Aprova as faturas em revisão"'
            hint="list_invoices + approve_invoice (com confirmação)"
          />
        </div>
      </Section>
    </>
  );
}

function Section({
  step,
  title,
  description,
  children,
}: {
  step: number;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-8">
      <header className="mb-4 flex items-start gap-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary-600 text-white text-sm font-bold shrink-0">
          {step}
        </div>
        <div>
          <h2 className="font-semibold text-lg">{title}</h2>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </header>
      {children}
    </section>
  );
}

function AgentCard({ agent, apiKey }: { agent: AgentDef; apiKey: string }) {
  const [copied, setCopied] = useState(false);
  const config = useMemo(() => agent.buildConfig(apiKey), [agent, apiKey]);
  const cli = useMemo(() => agent.buildCli?.(apiKey), [agent, apiKey]);
  const deepLink = useMemo(() => agent.deepLink?.(apiKey), [agent, apiKey]);

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast.success("Copiado");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Badge variant="primary" className="capitalize">{agent.configType}</Badge>
          {agent.docs && (
            <a
              href={agent.docs}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-primary-700 hover:underline inline-flex items-center gap-1"
            >
              docs <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
        <div className="flex items-center gap-2">
          {deepLink && (
            <Button asChild>
              <a href={deepLink}>
                <Plug className="h-4 w-4" /> Abrir no {agent.name.split(" ")[0]}
              </a>
            </Button>
          )}
        </div>
      </div>

      {cli && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
            Via CLI (mais rápido)
          </p>
          <CodeBlock
            code={cli}
            language="bash"
            onCopy={() => copy(cli)}
            copied={copied}
          />
        </div>
      )}

      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
          Config manual ({agent.configType.toUpperCase()})
        </p>
        <CodeBlock
          code={config}
          language={agent.configType}
          onCopy={() => copy(config)}
          copied={copied}
        />
      </div>

      <details className="rounded-xl border border-slate-200/60 dark:border-white/10 p-3 group">
        <summary className="text-xs font-medium cursor-pointer">Pré-requisito: `uvx` instalado</summary>
        <p className="mt-2 text-xs text-muted-foreground">
          O <code className="font-mono">uvx</code> roda o MCP server sem precisar de virtualenv. Instale uv com:
        </p>
        <CodeBlock
          code="curl -LsSf https://astral.sh/uv/install.sh | sh"
          language="bash"
          small
        />
      </details>
    </div>
  );
}

function CodeBlock({
  code,
  language,
  onCopy,
  copied,
  small,
}: {
  code: string;
  language: string;
  onCopy?: () => void;
  copied?: boolean;
  small?: boolean;
}) {
  return (
    <div className="relative group">
      <pre
        className={cn(
          "rounded-xl bg-slate-900 dark:bg-black/60 text-slate-100 p-4 overflow-x-auto",
          small ? "text-[11px]" : "text-xs",
        )}
      >
        <code className="font-mono">{code}</code>
      </pre>
      {onCopy && (
        <button
          type="button"
          onClick={onCopy}
          className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-100 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
        >
          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          {copied ? "Copiado" : "Copiar"}
          <span className="ml-1 opacity-50">{language}</span>
        </button>
      )}
    </div>
  );
}

function ExampleCard({ text, hint }: { text: string; hint: string }) {
  return (
    <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 p-4">
      <p className="text-sm font-medium italic">{text}</p>
      <p className="text-[11px] text-muted-foreground mt-2">→ {hint}</p>
    </div>
  );
}

// Empty state placeholder pra silenciar unused
void EmptyState;
